import * as React from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import {
  Bell,
  Blocks,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  Compass,
  FileText,
  Globe,
  LayoutDashboard,
  LogOut,
  MapPin,
  PanelLeft,
  Route as RouteIcon,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";
import type { Capability, UserWithContext } from "@taskiq/shared";
import { TaskIqLogo } from "@/components/Brand";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PersonAvatar } from "@/components/ui/Avatar";
import { IconButton } from "@/components/ui/IconButton";
import { Menu, MenuItem, MenuLabel, MenuSeparator } from "@/components/ui/Menu";
import { useConfirm } from "@/components/ui/confirm-context";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/cn";
import { ScopeSwitcher } from "./ScopeSwitcher";
import { ThemeToggle } from "./ThemeToggle";

/**
 * The authenticated shell: a deep rail and a light working surface on desktop,
 * a bottom tab bar on a phone.
 *
 * **Not the marketing site's layout, deliberately.** A visitor is browsing and
 * needs breadth across the top; an administrator is working and needs navigation
 * that stays put while the content changes under it. The two shells share the
 * design tokens and nothing else.
 *
 * The rail is on the deep panel in both themes, the way the Visionify Safety
 * Platform's is. It
 * is chrome rather than content, and a surface that does not follow the page is a
 * surface the eye stops re-reading - which is the entire point of a rail somebody
 * looks at forty times a day.
 *
 * Navigation is a module-level array filtered by capability, not a tree of
 * `{role === "admin" && ...}` conditionals. A field officer and an admin get
 * genuinely different consoles from the same source, and adding a fourth role
 * later means one entry in `ROLE_CAPABILITIES` rather than a grep through here.
 */

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Hidden entirely when the user lacks this. */
  capability?: Capability;
  /**
   * Whether this appears in the phone's tab bar.
   *
   * The bar takes five items and silently drops the rest (`items.slice(0, 5)`),
   * which was fine while there were exactly five and became a bug the moment
   * there were seven: a superadmin lost two destinations on a phone with nothing
   * on screen saying so. Marking the desk-bound ones false makes the omission a
   * decision instead of an accident.
   */
  mobile?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Two groups, split by what the reader is there to do: **watch** the work, or
 * **change** the setup behind it.
 *
 * It used to be three - Monitor, Organisation, Platform - which split by what
 * the rows *are* rather than by why anyone opened them, and left Companies alone
 * in a group of one that only Visionify staff ever saw. Sites, Team and
 * Companies are all the same errand: somebody is editing the shape of the
 * account, and they belong together whether or not the reader can see all three.
 */
const NAV: NavGroup[] = [
  {
    label: "Monitor",
    items: [
      { to: "/app", label: "Overview", icon: LayoutDashboard },
      // Schedule before Visits before Assessments: the order the work moves
      // through, so the rail reads as the product's own sequence rather than as
      // an alphabetical list of nouns.
      {
        to: "/app/schedule",
        label: "Schedule",
        icon: CalendarDays,
        capability: "appointment.read",
      },
      { to: "/app/visits", label: "Visits", icon: RouteIcon, capability: "visit.read" },
      {
        to: "/app/assessments",
        label: "Assessments",
        icon: ClipboardCheck,
        capability: "assessment.read",
      },
    ],
  },
  {
    label: "Intelligence",
    items: [
      {
        to: "/app/reports",
        label: "Reports",
        icon: FileText,
        capability: "report.export",
        mobile: false,
      },
    ],
  },
  {
    label: "Manage",
    items: [
      // Gated on the WRITE capability, not the read one, and that is the
      // difference between "may you see this data" and "is there a page here
      // for you". A field officer holds `user.read` and `site.read` - the
      // assignee picker and the technician's name on a visit both need them -
      // but every control on these two screens is behind `user.write` /
      // `site.write`, so for them the pages render as a roster and a list with
      // nothing to press. A nav item that leads somewhere inert is worse than
      // no nav item.
      //
      // The pages themselves are not access-controlled by this. Reaching
      // /app/team by URL still works and still shows what RLS allows; this is
      // wayfinding, not a boundary. The boundary is `users_update` and
      // `sites_update`, which refuse them regardless.
      { to: "/app/team", label: "Team", icon: Users, capability: "user.write" },
      { to: "/app/sites", label: "Locations", icon: MapPin, capability: "site.write" },
      // Off the phone bar. Provisioning a customer organisation is desk work,
      // and it is the only item here a field officer never sees anyway - so it
      // is the right one to drop when the bar runs out of room.
      {
        to: "/app/companies",
        label: "Workspaces",
        icon: Building2,
        capability: "platform.admin",
        mobile: false,
      },
      // The prompt and the settings every customer's checks run with. Global,
      // so Visionify's alone, and a textarea of prompt is desk work.
      {
        to: "/app/modules",
        label: "Modules",
        icon: Blocks,
        capability: "platform.admin",
        mobile: false,
      },
    ],
  },
];

const COLLAPSE_KEY = "taskiq.sidebar.collapsed";

export function AppShell() {
  const { can, user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useCollapsedRail();

  const confirm = useConfirm();

  /**
   * Signing out asks, and it did not used to.
   *
   * A one-press sign-out sat in a menu two rows below "Profile", and the phone
   * has confirmed it since it was built. The asymmetry is what made it worth
   * fixing: on a shared crew tablet the press ends somebody else's shift as
   * well, and getting back in costs a password nobody carries into a truck.
   */
  const handleSignOut = React.useCallback(async () => {
    const ok = await confirm({
      title: "Sign out?",
      message: "This browser only. Anybody signed in on their own device stays signed in.",
      confirmLabel: "Sign out",
      cancelLabel: "Stay signed in",
      danger: true,
    });
    if (!ok) return;

    await signOut();
    navigate("/login", { replace: true });
  }, [confirm, signOut, navigate]);

  const groups = React.useMemo(
    () =>
      NAV.map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.capability || can(item.capability)),
      })).filter((group) => group.items.length > 0),
    [can]
  );

  const flat = groups.flatMap((group) => group.items);

  return (
    <div
      className="shell"
      style={{
        gridTemplateColumns: `${collapsed ? "var(--size-sidebar-width-collapsed)" : "var(--size-sidebar-width)"} 1fr`,
      }}
    >
      <Sidebar groups={groups} collapsed={collapsed} />

      <div className="shell__main-col">
        <Topbar
          collapsed={collapsed}
          onToggleRail={() => setCollapsed(!collapsed)}
          user={user}
          onSignOut={handleSignOut}
        />
        {/* Bottom padding clears the mobile tab bar plus the home indicator. */}
        <main className="shell__main">
          <div className="shell__measure">
            {/* Inside the shell rather than around the app, so a page that
              throws leaves the rail and the topbar standing and the reader can
              navigate away from it. Keyed on the path so the boundary resets on
              navigation instead of latching the previous page's failure. */}
            <ErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      <MobileNav items={flat.filter((item) => item.mobile !== false)} />
    </div>
  );
}

/* ──────────────────────────────── the rail ──────────────────────────────── */

function Sidebar({ groups, collapsed }: { groups: NavGroup[]; collapsed: boolean }) {
  return (
    <aside className="on-panel rail">
      <div className={cn("rail__brand", collapsed && "rail__brand--collapsed")}>
        <Link to="/app" aria-label="TaskIQ home">
          <TaskIqLogo tone="panel" markOnly={collapsed} />
        </Link>
      </div>

      <nav className="rail__nav">
        {groups.map((group) => (
          <div key={group.label} className="rail__group">
            {/* Collapsed, the label has nowhere to go - a rule stands in for it
                so the groups are still visibly separate at 72px. */}
            {collapsed ? (
              <div className="rail__group-rule" />
            ) : (
              <p className="eyebrow eyebrow--sm eyebrow--panel-subtle rail__group-label">
                {group.label}
              </p>
            )}

            <ul className="rail__items">
              {group.items.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    // `end` on the index route only, or /app would stay
                    // highlighted on every child route beneath it.
                    end={to === "/app"}
                    title={collapsed ? label : undefined}
                    className={({ isActive }) =>
                      cn(
                        "rail__link",
                        collapsed && "rail__link--collapsed",
                        isActive && "rail__link--active"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {/* Inside the item's own box, not hanging off its left
                            edge - the nav scrolls, and an `overflow-y: auto`
                            ancestor clips anything with a negative offset. */}
                        {isActive && !collapsed && <span aria-hidden className="rail__marker" />}
                        <Icon />
                        {!collapsed && label}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/*
        Tenancy, not navigation - which company and which site the whole
        console is looking at right now. It sits apart from the nav list and
        pinned to the bottom, the way a workspace switcher sits in Linear or
        Vercel: the one control that changes what everything above it means,
        not another row of "go here".
        Collapsed to 72px there is no room to lay out two full-width chips
        legibly, so it hides rather than truncate into something unreadable -
        expand the rail to switch.
      */}
      {!collapsed && (
        <div className="rail__scope">
          <ScopeSwitcher />
        </div>
      )}
    </aside>
  );
}

/* ─────────────────────────────── the top bar ─────────────────────────────── */

/**
 * Chrome, and only chrome: where you are working, and how the page looks.
 *
 * The account menu lives here now, top right, the same corner it sits in on
 * almost every SaaS console - it is chrome about the *session*, not about the
 * tenant, so it stays with theme rather than with the scope switcher in the
 * rail. One trigger, always visible regardless of viewport, which is also
 * what let the phone-only sign-out button that used to live here go away.
 */
function Topbar({
  collapsed,
  onToggleRail,
  user,
  onSignOut,
}: {
  collapsed: boolean;
  onToggleRail: () => void;
  user: UserWithContext | null;
  onSignOut: () => Promise<void>;
}) {
  return (
    <header className="topbar">
      <IconButton
        onClick={onToggleRail}
        label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!collapsed}
        className={cn("topbar__rail-toggle", collapsed && "topbar__rail-toggle--collapsed")}
      >
        <PanelLeft />
      </IconButton>

      {/* The rail is gone on a phone, so the wordmark moves up here and doubles
          as the way home. */}
      <Link to="/app" className="topbar__brand" aria-label="TaskIQ home">
        <TaskIqLogo size="sm" />
      </Link>

      <div className="topbar__spacer" />

      <div className="topbar__actions">
        {/* Not wired up yet - visual placeholders. */}
        <IconButton label="Change language" onClick={() => {}} className="topbar__language">
          <Globe />
          <span className="topbar__language-code">EN</span>
        </IconButton>
        <IconButton label="AI assistant" onClick={() => {}}>
          <Sparkles />
        </IconButton>
        <IconButton label="Take a tour" onClick={() => {}}>
          <Compass />
        </IconButton>
        <ThemeToggle />
        <IconButton label="Notifications" onClick={() => {}}>
          <Bell />
        </IconButton>
      </div>

      {user && <AccountMenu user={user} onSignOut={onSignOut} />}
    </header>
  );
}

/**
 * The account's own pages plus sign-out, as a dropdown behind the avatar
 * rather than a link with a chevron. It used to be a row in the rail that
 * navigated to `/app/profile` on click, with sign-out as a second, separate
 * control beside it - two targets for two intentions, one of them
 * irreversible. A menu keeps that same separation - sign-out sits behind its
 * own divider, never guessable from the two links above it - without needing
 * a whole row of rail real estate to say so.
 */
function AccountMenu({
  user,
  onSignOut,
}: {
  user: UserWithContext;
  onSignOut: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const name = user.full_name ?? user.email;

  return (
    <Menu
      align="end"
      className="topbar__account"
      trigger={
        <>
          <PersonAvatar person={user} size="sm" />
          <span className="topbar__account-name">{name}</span>
          <ChevronDown aria-hidden className="topbar__account-chevron" />
        </>
      }
    >
      <MenuLabel>{name}</MenuLabel>
      <MenuSeparator />
      <MenuItem icon={<UserRound />} onClick={() => navigate("/app/profile")}>
        Profile
      </MenuItem>
      <MenuItem icon={<ShieldCheck />} onClick={() => navigate("/app/profile?tab=security")}>
        Sign-in and security
      </MenuItem>
      <MenuSeparator />
      <MenuItem icon={<LogOut />} tone="danger" onClick={() => void onSignOut()}>
        Sign out
      </MenuItem>
    </Menu>
  );
}

/* ────────────────────────────── the phone bar ────────────────────────────── */

/**
 * Bottom tab bar. Five items maximum and no overflow menu - a technician holding
 * a phone one-handed in a truck should reach every destination with a thumb, and
 * a "More" sheet defeats that.
 *
 * The cap is still a silent truncation, so **which** five is decided upstream by
 * `NavItem.mobile` rather than by whatever order the array happens to be in.
 * Before that flag existed the rail had exactly five items and the slice was
 * invisible; the day it grew to seven, two destinations vanished from every
 * phone with nothing on screen saying why.
 */
function MobileNav({ items }: { items: NavItem[] }) {
  return (
    <nav className="tabbar">
      <ul className="tabbar__list">
        {items.slice(0, 5).map(({ to, label, icon: Icon }) => (
          <li key={to} className="tabbar__item">
            <NavLink
              to={to}
              end={to === "/app"}
              className={({ isActive }) => cn("tabbar__link", isActive && "tabbar__link--active")}
            >
              <Icon />
              {label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* ──────────────────────────────── helpers ──────────────────────────────── */

/**
 * The rail's width, remembered.
 *
 * Read from storage in the initialiser rather than in an effect: setting it
 * afterwards would render the expanded rail for one frame and then snap it
 * shut, which reads as a layout bug every single time the console is opened.
 */
function useCollapsedRail(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const set = React.useCallback((next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
    } catch {
      /* storage unavailable - the choice still applies for this session */
    }
  }, []);

  return [collapsed, set];
}
